import { Box, Button, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getOrganizationFee, updateOrganizationFee } from 'src/shared/services/organizationService';
import { getPaymentMethodOptions, PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import ControlledRadioButton from 'src/shared/components/form/controlledRadioButton';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { PaymentMethodOption } from 'src/modules/associations/models/associationFee';
import { AssociationSendingMethodHandler } from 'src/modules/associations/components/associationSendingMethodHandler';
import { AssociationFeeProfileHandler } from 'src/modules/associations/components/associationFeeProfileHandler';
import { Add } from '@mui/icons-material';
import { OrganizationFeeRequest } from '../models/organizationFeeRequest';

export const OrganizationFeePage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);

  // Tillsvidare sätts allt till readonly
  const readOnlyMode = true;
  const paymentMethodOptions = getPaymentMethodOptions(t);

  const OrganizationRequestSchema: Schema<OrganizationFeeRequest> = yup.object().shape({
    paymentMethod: yup.number().required(),
    ocr: yup.string().oneOf(['true', 'false']).required(),
    // Aviseringsmetod
    sameFeeForAll: yup.number().required(),
    mainAndFamily: yup
      .object({
        feeMainMember: yup.number().required(),
        feeFamilyMember: yup.number().required(),
      })
      .optional(),
    familySteps: yup
      .array()
      .of(
        yup.object().shape({
          member: yup.string().optional(),
          fee: yup.number().required(t('association:paymentMethod.feeRequired')),
        }),
      )
      .optional(),

    oneFeeForHousehold: yup.number().required(),
    // Profiler
    feeBasedOnProfile: yup
      .array()
      .of(
        yup.object().shape({
          memberProfileItem_id: yup.number().required(),
          profileGroup: yup.string().required(),
          profile: yup.string().required(),
          fee: yup.number().required(t('association:paymentMethod.feeRequired')),
        }),
      )
      .optional()
      .nullable(),
  });

  const formMethods = useForm<OrganizationFeeRequest>({
    resolver: yupResolver(OrganizationRequestSchema),
    mode: 'all',
    defaultValues: {
      paymentMethod: 0,
      ocr: 'false',
      sameFeeForAll: 0,
      mainAndFamily: {
        feeMainMember: 0,
        feeFamilyMember: 0,
      },
      familySteps: [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: 0,
      feeBasedOnProfile: undefined,
    },
  });

  const { handleSubmit, reset, setValue, watch } = formMethods;

  const paymentMethod = watch('paymentMethod');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getOrganizationFee();
      if (isMounted()) {
        setValue('paymentMethod', result.data.paymentMethod);
        setValue('feeBasedOnProfile', result.data.feeBasedOnProfile);
        if (result.data.mainAndFamily !== null) setValue('mainAndFamily', result.data.mainAndFamily);
        if (result.data.familySteps !== null) setValue('familySteps', result.data.familySteps);
        setValue('sameFeeForAll', result.data.sameFeeForAll);
        setValue('oneFeeForHousehold', result.data.oneFeeForHousehold);
        setValue('ocr', result.data.ocr.toString());
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, setValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPaymentMethod = paymentMethodOptions.find((opt) => opt.value === watch('paymentMethod'));

  const FieldsToClean: Record<PaymentMethodEnum, Array<keyof OrganizationFeeRequest>> = {
    [PaymentMethodEnum.SameFeeForAll]: ['mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.MainAndFamily]: ['sameFeeForAll', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.FamilySteps]: ['sameFeeForAll', 'mainAndFamily', 'oneFeeForHousehold'],
    [PaymentMethodEnum.OneFeeForHousehold]: ['sameFeeForAll', 'mainAndFamily', 'familySteps'],
    [PaymentMethodEnum.FeeBasedOnProfile]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.None]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
  };

  const onSubmit = async (data: OrganizationFeeRequest) => {
    setLoading(true);

    const cleanedData: OrganizationFeeRequest = {
      ...data,
      ocr: data.ocr === 'true',
    };
    if (typeof paymentMethod !== 'number') return;
    FieldsToClean[paymentMethod as PaymentMethodEnum]?.forEach((field) => {
      (cleanedData as Partial<OrganizationFeeRequest>)[field] = undefined;
    });

    try {
      await updateOrganizationFee(cleanedData);
      if (isMounted()) {
        reset();
        toast.success(<>{t('association:feeUpdateSuccess')}</>);
        fetchData();
      }
    } catch (e: any) {
      if (isMounted()) {
        const errorMessage = e?.data?.messages;
        toast.error(<>{errorMessage}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 2 }}>
            <Grid container>
              {!readOnlyMode && (
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <ControlledRadioButton
                      name='ocr'
                      disabled={readOnlyMode}
                      label={t('association:useOcr')}
                      items={[
                        {
                          value: 'true',
                          label: t('common:button.yes'),
                        },
                        {
                          value: 'false',
                          label: t('common:button.no'),
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container mt={1} spacing={2}>
                <Grid item xs={10} md={4} sx={{ mb: { xs: -5, md: 0 } }}>
                  <AutocompleteDropdown<PaymentMethodOption>
                    options={paymentMethodOptions}
                    disable={readOnlyMode}
                    name='paymentMethod'
                    nameProp='description'
                    placeholder={t('association:paymentMethod.none')}
                    emptyLabel={t('association:paymentMethod.none')}
                    value={selectedPaymentMethod}
                    keyProp='id'
                    label='Aviseringsmetod'
                    callback={(val) => {
                      setValue('paymentMethod', val?.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7} ml={2} sx={{ ml: { xs: 0, md: 2 }, mt: { xs: 5, md: 0 } }}>
                  <AssociationSendingMethodHandler readOnlyMode={readOnlyMode} paymentMethod={paymentMethod || 0} />
                </Grid>
              </Grid>
              <Grid item xs={11} mt={5}>
                <AssociationFeeProfileHandler readOnlyMode={readOnlyMode} openAddprofile={openAddprofile} setOpenAddprofile={setOpenAddprofile} />
              </Grid>
              {!readOnlyMode && (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, mt: 2 }}>
                  <Button
                    variant='outlined'
                    size='small'
                    disableElevation
                    onClick={() => {
                      setOpenAddprofile(true);
                    }}
                  >
                    <Add fontSize='small' />
                    {t('association:addProfile')}
                  </Button>

                  <Button
                    disabled={paymentMethod === undefined || paymentMethod === 0}
                    sx={{ marginLeft: 2 }}
                    type='submit'
                    variant='contained'
                    disableElevation
                  >
                    {t('common:button.save')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
